import { environment } from '@/environments/environment';
import pdfMake from 'pdfmake';
import {
  Content,
  DynamicContent,
  TDocumentDefinitions,
} from 'pdfmake/interfaces';
import { Pdf } from '../model/table.model';
import { formatDate } from '@/app/core/tools/format-date';
import { imgToBase64 } from '@/app/core/tools/url-to-base64';

export async function downloadPdf<D = any>(config: Pdf<D>, data: D[]) {
  const pM = (pdfMake as any).default || pdfMake;

  pM.fonts = {
    Roboto: {
      normal: environment.webUrl + 'fonts/urbanist-regular.ttf',
      bold: environment.webUrl + 'fonts/urbanist-bold.ttf',
      italics: environment.webUrl + 'fonts/urbanist-italic.ttf',
      bolditalics: environment.webUrl + 'fonts/urbanist-bold.ttf',
    },
  };

  // Pie de página con número de página actual / total
  const footer: DynamicContent | Content = (
    currentPage: number,
    pageCount: number,
  ) => ({
    text: `Página ${currentPage} de ${pageCount}`,
    alignment: 'right',
    margin: [20, 10],
    fontSize: 9,
  });

  // Fila de encabezado de la tabla, armada dinámicamente a partir de config.columns
  const headerRow = config.columns.map((c) => ({
    text: c.header,
    bold: true,
    fontSize: 9,
    color: 'white',
    fillColor: '#900C23',
  }));

  // Filas de datos: si la columna define un `value(item)`, se usa esa transformación
  // (ej. para formatear fechas o concatenar nombre + apellido); si no, se lee `item[key]` directo
  const bodyRows = data.map((item) =>
    config.columns.map((c) => ({
      text: c.value ? c.value(item) : ((item as any)[c.key] ?? '').toString(),
      fontSize: 8,
    })),
  );

  const d: TDocumentDefinitions = {
    pageOrientation: config.orientation ?? 'landscape',
    content: [
      // Header del documento: logo + título de la tabla + fecha de impresión
      {
        margin: [0, 0, 0, 10],
        table: {
          widths: ['10%', '90%', '12%'],
          body: [
            [
              {
                image: (await imgToBase64(environment.webUrl + 'logo-black.jpg')) ??'',
                width: 30,
                margin: 10,
              },
              [
                { text: config.title, bold: true, fontSize: 16, marginTop: 5, marginBottom: 5 },
                {
                  text: [
                    { text: 'Fecha de impresión: ', bold: true, fontSize: 10 },
                    { text: formatDate(new Date()) ?? '', fontSize: 10 },
                  ],
                },
              ],
            ],
          ],
        },
        layout: {
          paddingLeft: function (i, node) {
            return 10;
          },
        },
      },
      // Tabla principal con los datos exportados
      {
        table: {
          headerRows: 1,
          widths: config.columns.map((c) => c.width ?? '*'),
          body: [headerRow, ...bodyRows],
        },
        layout: {
          hLineWidth: function (i, node) {
            return i === 0 || i === node.table.body.length ? 0 : 0.4;
          },
          // Mantiene el color gris tenue de las líneas
          hLineColor: function (i, node) {
            return '#e0e0e0';
          },
          // Elimina las líneas verticales
          vLineWidth: function (i, node) {
            return 0;
          },
          paddingTop: function (i, node) {
            return 4;
          },
          paddingBottom: function (i, node) {
            return 4;
          },
          paddingLeft: function (i, node) {
            return 5;
          },
        },
      },
    ],
    footer,
  };

  // .download(filename) fuerza la descarga con el nombre indicado en filename
  pM.createPdf(d).download(`${config.filename}.pdf`);
}
